import { useState } from 'react';
import { useDeleteDraftInvoice, useSendInvoice, useStudioProjects } from '@/api/studio';
import type { Invoice } from '@/api/types';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dialog } from '@/components/ui';
import { formatMoney } from '@/lib/format';
import { IssueInvoiceDialog } from './IssueInvoice';

/** A draft invoice is the studio's own: edit it, send it to the client, or throw it away. */
export function InvoiceDraftActions({ invoice }: { invoice: Invoice }) {
  const send = useSendInvoice();
  const del = useDeleteDraftInvoice();
  const { data: projects } = useStudioProjects(invoice.clientId ?? undefined);
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sending, setSending] = useState(false);
  const error = (send.error ?? del.error) as Error | null;
  return (
    <>
      <button className="btn-ghost h-7 px-2 text-xs" disabled={!projects || !invoice.clientId} onClick={() => setEditing(true)}>
        Изменить
      </button>
      <button className="btn-secondary h-7 px-2 text-xs" disabled={send.isPending} onClick={() => setSending(true)}>
        Отправить клиенту
      </button>
      <button className="btn-ghost h-7 px-2 text-xs text-overdue" onClick={() => setDeleting(true)}>
        Удалить
      </button>
      {error && (
        <span role="alert" className="basis-full text-right text-xs text-overdue">
          {error.message}
        </span>
      )}
      {projects && invoice.clientId && <IssueInvoiceDialog clientId={invoice.clientId} projects={projects} draft={invoice} open={editing} onClose={() => setEditing(false)} />}
      <ConfirmDialog
        open={sending}
        onClose={() => setSending(false)}
        title="Отправить счёт клиенту?"
        confirmLabel="Отправить"
        pending={send.isPending}
        onConfirm={() => send.mutate(invoice.id, { onSettled: () => setSending(false) })}
      >
        Счёт № {invoice.number} на {formatMoney(invoice.total)} станет виден клиенту, он получит уведомление. Править после отправки нельзя, только отменить.
      </ConfirmDialog>
      <Dialog open={deleting} onClose={() => setDeleting(false)} title="Удалить черновик счёта?">
        <div className="space-y-4 p-5 text-sm">
          <p>
            Черновик № {invoice.number} на {formatMoney(invoice.total)} будет удалён. Клиент его не видел.
          </p>
          <div className="flex gap-2">
            <button className="btn-secondary flex-1" onClick={() => setDeleting(false)}>
              Оставить
            </button>
            <button className="btn-primary flex-1" disabled={del.isPending} onClick={() => del.mutate(invoice.id, { onSuccess: () => setDeleting(false) })}>
              Удалить
            </button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
